import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "../ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
    updateHourLogConfig,
    sendHourLogReportNow,
    listWorkgroups, listWorkgroupMembers,
} from "../../lib/api";

export default function SettingsDialog({ open, onClose, config, onSaved }) {
    const [payInterval, setPayInterval] = useState(config?.pay_interval || "biweekly");
    const [payDay, setPayDay] = useState(config?.pay_day ?? 4);
    const [wgId, setWgId] = useState(config?.workgroup_id || "");
    const [reportTo, setReportTo] = useState(config?.send_report_to_user_id || "");
    const [sharedIds, setSharedIds] = useState(config?.shared_member_user_ids || []);
    const [wgs, setWgs] = useState([]);
    const [members, setMembers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        listWorkgroups().then(setWgs).catch(() => setWgs([]));
    }, []);
    useEffect(() => {
        if (!wgId) { setMembers([]); return; }
        listWorkgroupMembers(wgId).then(setMembers).catch(() => setMembers([]));
    }, [wgId]);

    const save = async () => {
        setBusy(true);
        try {
            const c = await updateHourLogConfig({
                pay_interval: payInterval,
                pay_day: Number(payDay),
                workgroup_id: wgId || null,
                send_report_to_user_id: reportTo || null,
                shared_member_user_ids: sharedIds,
            });
            onSaved(c);
            toast.success("Settings saved");
        } catch { toast.error("Couldn't save"); }
        finally { setBusy(false); }
    };
    const sendNow = async () => {
        setSending(true);
        try {
            const r = await sendHourLogReportNow();
            if (r?.sent_id) toast.success(`Report emailed to ${r.recipient_email}`);
            else toast.message("Report generated — no email recipient resolved");
        } catch { toast.error("Couldn't send"); }
        finally { setSending(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="bg-[#131A2F] border border-[#2A3655] text-white max-w-lg"
                data-testid="hour-log-settings"
            >
                <DialogHeader>
                    <DialogTitle className="text-white">Hour log settings</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Pay interval</label>
                        <select value={payInterval} onChange={(e) => setPayInterval(e.target.value)} data-testid="select-pay-interval" className="w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-3 py-2 text-sm mt-1">
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Bi-weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                    {payInterval === "monthly" ? (
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Pay day (day of month)</label>
                            <Input
                                type="number" min={1} max={31}
                                value={payDay}
                                onChange={(e) => setPayDay(e.target.value)}
                                data-testid="input-pay-day"
                                className="bg-[#0A1128] border-[#2A3655] mt-1"
                            />
                            <div className="text-[10px] text-slate-500 mt-1">
                                If the chosen day doesn't exist in a given month, the report fires on the last day.
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Pay day</label>
                            <div className="grid grid-cols-7 gap-1 mt-1" data-testid="pay-day-picker">
                                {[
                                    { d: 0, label: "Monday" },
                                    { d: 1, label: "Tuesday" },
                                    { d: 2, label: "Wednesday" },
                                    { d: 3, label: "Thursday" },
                                    { d: 4, label: "Friday" },
                                    { d: 5, label: "Saturday" },
                                    { d: 6, label: "Sunday" },
                                ].map((opt) => {
                                    const on = Number(payDay) === opt.d;
                                    return (
                                        <button
                                            key={opt.d}
                                            type="button"
                                            onClick={() => setPayDay(opt.d)}
                                            data-testid={`pay-day-${opt.d}`}
                                            title={opt.label}
                                            className={`px-1.5 py-2 rounded-sm text-[10px] uppercase tracking-wider font-bold border transition-colors ${
                                                on
                                                    ? "bg-[#00A3FF] text-[#0A1128] border-[#00A3FF]"
                                                    : "bg-[#1C253C] border-[#2A3655] text-slate-300 hover:border-[#00A3FF] hover:text-white"
                                            }`}
                                        >
                                            {opt.label.slice(0, 3)}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1">
                                Reports email automatically at 5:00 PM on the selected day.
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Workgroup (for sharing)</label>
                        <select value={wgId} onChange={(e) => setWgId(e.target.value)} data-testid="settings-workgroup" className="w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-3 py-2 text-sm mt-1">
                            <option value="">— Personal only —</option>
                            {wgs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                        </select>
                    </div>
                    {wgId && (
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Share with specific members (leave empty = whole workgroup)</label>
                            <div className="mt-1 max-h-48 overflow-auto bg-[#0A1128] border border-[#2A3655] rounded-sm">
                                {members.map((m) => {
                                    const on = sharedIds.includes(m.user_id);
                                    return (
                                        <button key={m.user_id} type="button" onClick={() => setSharedIds((s) => on ? s.filter((x) => x !== m.user_id) : [...s, m.user_id])} data-testid={`toggle-shared-${m.user_id}`} className={`w-full text-left px-3 py-2 border-b border-[#2A3655] last:border-0 ${on ? "bg-[#00A3FF]/15" : "hover:bg-[#1C253C]"}`}>
                                            <div className="text-sm text-white flex items-center justify-between">
                                                {m.name || m.email}
                                                <span className={`text-[10px] uppercase font-bold ${on ? "text-[#00A3FF]" : "text-slate-500"}`}>{on ? "shared" : "—"}</span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Send pay report to</label>
                        <select value={reportTo} onChange={(e) => setReportTo(e.target.value)} data-testid="settings-report-to" className="w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-3 py-2 text-sm mt-1">
                            <option value="">— Me (the owner) —</option>
                            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                        </select>
                    </div>
                    <button onClick={sendNow} disabled={sending} data-testid="btn-send-report-now" className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-[#7B5BFF]/20 border border-[#7B5BFF]/50 text-[#A98EFF] hover:bg-[#7B5BFF]/30 text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50">
                        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Send report now (test)
                    </button>
                </div>
                <DialogFooter>
                    <button onClick={onClose} className="px-4 py-2 text-xs uppercase font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="btn-save-settings" className="px-4 py-2 text-xs uppercase font-bold rounded-sm bg-[#00A3FF]/20 border border-[#00A3FF]/50 text-[#00A3FF] hover:bg-[#00A3FF]/30 disabled:opacity-50">
                        {busy ? "Saving…" : "Save"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
